import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BadgeIcon from '@mui/icons-material/Badge'
import ChatIcon from '@mui/icons-material/Chat'
import VerifiedIcon from '@mui/icons-material/Verified'
import EmailIcon from '@mui/icons-material/Email'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import api from '@/services/api'
import {
  Drawer,
  IconButton,
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
  Divider,
} from '@mui/material'

// Exact theme color palette from the user:
const COLOR_PRIMARY = '#7A6FA1'
const COLOR_GREEN_SUCCESS = '#3BAE78'
const COLOR_RED_DANGER = '#E0451A'
const COLOR_SECTION_BG = '#E7E3F266'
const COLOR_ORANGE_PENDING = '#FF9C07'
const COLOR_LIGHT_PURPLE = '#7A6FA133'

const formatDate = (date: string | Date | null | undefined) => {
  if (!date) return null
  const d = dayjs(date)
  if (!d.isValid()) return null
  return d.format('MM/DD/YYYY')
}

export interface PendingApprovalReviewDialogProps {
  open: boolean
  onClose: () => void
  approval: any
  onApprove?: (approvalId: string) => void
  onReject?: (approvalId: string) => void
}

/** A single label → value row inside a section card */
const DataRow = ({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      py: 1.2,
      '&:not(:last-child)': {
        borderBottom: '1px solid rgba(122, 111, 161, 0.1)',
      },
    }}
  >
    <Typography sx={{ fontSize: '0.88rem', color: '#574F75', fontWeight: 500, mt: 0.2 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: '0.88rem', color: '#7A6FA1', fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Box>
  </Box>
)

/** Section container with icon + title heading */
const Section = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
      <Box sx={{ color: COLOR_PRIMARY, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: COLOR_PRIMARY }}>
        {title}
      </Typography>
    </Box>
    <Box
      sx={{
        bgcolor: COLOR_SECTION_BG,
        borderRadius: 2,
        px: 2.5,
        py: 0.5,
      }}
    >
      {children}
    </Box>
  </Box>
)

export const PendingApprovalReviewDialog: React.FC<PendingApprovalReviewDialogProps> = ({
  open,
  onClose,
  approval,
  onApprove,
  onReject,
}) => {
  if (!approval) return null

  // Support both flat and nested `provider` structures
  const p = approval?.providerId || approval?.provider || approval || {}

  const [specializationNames, setSpecializationNames] = useState<string[]>([])

  useEffect(() => {
    const fetchSpecializations = async () => {
      const specializations = p?.specialization || []
      if (!specializations || specializations.length === 0) {
        setSpecializationNames([])
        return
      }

      // Some might already be objects with .name, some are string IDs
      const rawNames = specializations
        .filter((s: any) => typeof s === 'object' && s !== null && s.name)
        .map((s: any) => s.name)

      const idsToFetch = specializations.filter((s: any) => typeof s === 'string')

      if (idsToFetch.length === 0) {
        setSpecializationNames(rawNames)
        return
      }

      try {
        const response = await api.get('/specilities/specility')
        const allSpecs = response?.data?.data || response?.data || []

        const fetchedNames = idsToFetch.map((id: string) => {
          const found = allSpecs.find((spec: any) => spec._id === id || spec.id === id)
          return found ? found.name : id
        })

        setSpecializationNames([...rawNames, ...fetchedNames])
      } catch (err) {
        console.error('Error fetching specializations:', err)
        setSpecializationNames([...rawNames, ...idsToFetch])
      }
    }
    fetchSpecializations()
  }, [p?.specialization])


  console.log('Approval data in dialog:', p)



  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 9999,
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          borderLeft: '1.5px solid #E2E8F0',
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {p?.profilePhotoUrl || p?.profilePhoto || p?.photoUrl ? (
            <Avatar
              src={p?.profilePhotoUrl || p?.profilePhoto || p?.photoUrl}
              alt={(p?.firstName || p?.lastName) ? `${p?.firstName || ''} ${p?.lastName || ''}`.trim() : '__'}
              sx={{
                width: 64,
                height: 64,
                border: `2px solid ${COLOR_LIGHT_PURPLE}`,
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: COLOR_LIGHT_PURPLE,
                color: COLOR_PRIMARY,
                fontSize: '1.6rem',
                fontWeight: 700,
                border: `2px solid ${COLOR_LIGHT_PURPLE}`,
              }}
            >
              {((p?.firstName || p?.lastName) ? `${p?.firstName || ''} ${p?.lastName || ''}`.trim() : '__').charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#574F75' }}>
              {(p?.firstName || p?.lastName) ? `${p?.firstName || ''} ${p?.lastName || ''}`.trim() : '__'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.6, flexWrap: 'wrap' }}>
              {Array.isArray(specializationNames) && specializationNames.length > 0 && (
                specializationNames.map((spec, idx) => (
                  <Chip
                    key={idx}
                    label={spec}
                    size="small"
                    sx={{
                      bgcolor: COLOR_LIGHT_PURPLE,
                      color: COLOR_PRIMARY,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: 24,
                      px: 0.5,
                      borderRadius: '6px',
                    }}
                  />
                ))
              )}

              <Chip
                label="Pending Approval"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: COLOR_ORANGE_PENDING,
                  color: COLOR_ORANGE_PENDING,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  height: 24,
                  px: 0.5,
                  borderRadius: '12px',
                  borderWidth: '1.5px',
                }}
              />
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: '#94a3b8', '&:hover': { color: COLOR_PRIMARY, bgcolor: COLOR_LIGHT_PURPLE } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* ── Scrollable content ── */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2.5, bgcolor: 'white' }}>

        {/* Contact Info */}
        <Section
          icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
          title="Contact Info"
        >
          <DataRow label="Email" value={p?.email || '__'} />
          <DataRow label="Phone" value={p?.phone || '__'} />
          <DataRow label="Gender" value={p?.gender || '__'} />
          <DataRow label="Date of Birth" value={formatDate(p?.dob) || '__'} />
        </Section>

        {/* Professional Info */}
        <Section
          icon={<VerifiedIcon sx={{ fontSize: 18 }} />}
          title="Professional Info"
        >

          {specializationNames.length > 0 && (
            <DataRow
              label="Specialization"
              value={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                  {specializationNames.map((spec, idx) => (
                    <Typography key={idx} sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
                      {spec}
                    </Typography>
                  ))}
                </Box>
              }
            />
          )}
          <DataRow label="Years of Experience" value={p?.yearsOfExperience !== undefined && p?.yearsOfExperience !== null ? `${p.yearsOfExperience} years` : '__ '} />
          <DataRow
            label="Degrees"
            value={
              Array.isArray(p?.degrees) && p.degrees.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                  {p.degrees.map((d: any) => (typeof d === 'object' && d !== null ? d.name : d)).filter(Boolean).map((deg: string, idx: number) => (
                    <Typography key={idx} sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
                      {deg}
                    </Typography>
                  ))}
                </Box>
              ) : (
                typeof p?.degrees === 'string' && p?.degrees ? p.degrees : p?.qualification || '__'
              )
            }
          />
        </Section>

        {/* Location & Availability */}
        <Section
          icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
          title="Location"
        >
          <DataRow label="Address Line 1" value={p?.addressLine || '__'} />
          <DataRow label="Address Line 2" value={p?.addressLine2 || '--'} />
          <DataRow label="City & State" value={(p?.city && p?.state) ? `${p.city}, ${p.state}` : '--'} />
          <DataRow label="Zip Code" value={p?.zipCode || '__'} />
          {/* <DataRow label="Coverage Radius" value={p?.coverageRadius ? `${p.coverageRadius} miles` : '__'} />
          <DataRow
            label="Availability"
            value={
              (Array.isArray(p?.availability) ? p.availability : []).length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {(Array.isArray(p?.availability) ? p.availability : []).map((day: any) => typeof day === 'string' ? day.substring(0, 3) : '').filter(Boolean).map((day: string, idx: number) => (
                    <Typography
                      key={idx}
                      sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}
                    >
                      {day}
                    </Typography>
                  ))}
                </Box>
              ) : (
                '--'
              )
            }
          /> */}
        </Section>

        {/* License & Compliance */}
        {/* <Section
          icon={<BadgeIcon sx={{ fontSize: 18 }} />}
          title="License & Compliance"
        >
          <DataRow label="License Number" value={(Array.isArray(p?.licenses) ? p.licenses[0] : {})?.licenseNumber || p?.licenseNumber || p?.licenseNo || '__ '} />
          <DataRow label="Issued State" value={(Array.isArray(p?.licenses) ? p.licenses[0] : {})?.licenseState || p?.licenseState || p?.issuedState || p?.state || '__'} />
          <DataRow label="Expiry Date" value={formatDate((Array.isArray(p?.licenses) ? p.licenses[0] : {})?.expiryDate || p?.licenseExpiryDate || p?.expiryDate || p?.licenseExpiry) || '__'} />
          <DataRow
            label="Status"
            value={
              ((Array.isArray(p?.licenses) ? p.licenses[0] : {})?.licenseStatus || p?.licenseStatus) ? (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: `${COLOR_GREEN_SUCCESS} !important` }} />}
                  label={(Array.isArray(p?.licenses) ? p.licenses[0] : {})?.licenseStatus || p?.licenseStatus}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(59, 174, 120, 0.1)',
                    color: COLOR_GREEN_SUCCESS,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 24,
                    px: 0.5,
                    border: `1.5px solid rgba(59, 174, 120, 0.3)`,
                    '& .MuiChip-icon': { color: COLOR_GREEN_SUCCESS },
                  }}
                />
              ) : (
                '--'
              )
            }
          />
        </Section> */}

        {/* Insurance */}
        {Array.isArray(p?.insuranceAccepted) && p.insuranceAccepted.filter(Boolean).length > 0 && (
          <Section
            icon={<LocalHospitalIcon sx={{ fontSize: 18 }} />}
            title="Insurance Accepted"
          >
            <Box sx={{ py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {p.insuranceAccepted
                .map((ins: any) => (typeof ins === 'object' && ins !== null ? ins.payer_name : ins))
                .filter(Boolean)
                .map((name: string, idx: number) => (
                  <Chip
                    key={idx}
                    label={name}
                    size="small"
                    sx={{
                      bgcolor: COLOR_LIGHT_PURPLE,
                      color: COLOR_PRIMARY,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 26,
                      borderRadius: '8px',
                    }}
                  />
                ))}
            </Box>
          </Section>
        )}

        {/* Message from Provider */}
        {(approval?.message || p?.bio || p?.message) && (
          <Section
            icon={<ChatIcon sx={{ fontSize: 18 }} />}
            title="Message from Provider"
          >
            <Box
              sx={{
                py: 1.5,
                px: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.88rem',
                  color: '#475569',
                  lineHeight: 1.6,
                  fontStyle: 'italic',
                }}
              >
                "{approval?.message || p?.bio || p?.message}"
              </Typography>
            </Box>
          </Section>
        )}
      </Box>

      <Divider />

      {/* ── Action buttons ── */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: 'white',
          gap: 2,
          display: 'flex',
          justifyContent: 'stretch',
          '& > *': { flex: 1 },
        }}
      >
        <Button
          onClick={() => onReject && onReject(approval?._id || approval?.id || '')}
          variant="outlined"
          startIcon={<CancelIcon />}
          sx={{
            borderRadius: 2,
            py: 1.2,
            borderColor: COLOR_RED_DANGER,
            color: COLOR_RED_DANGER,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.92rem',
            '&:hover': {
              bgcolor: 'rgba(224, 69, 26, 0.06)',
              borderColor: COLOR_RED_DANGER,
            },
          }}
        >
          Reject Request
        </Button>
        <Button
          onClick={() => onApprove && onApprove(approval?._id || approval?.id || '')}
          variant="contained"
          startIcon={<CheckCircleIcon />}
          sx={{
            borderRadius: 2,
            py: 1.2,
            bgcolor: COLOR_GREEN_SUCCESS,
            color: 'white',
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.92rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#2e9e6a',
              boxShadow: `0 4px 12px rgba(59, 174, 120, 0.3)`,
            },
          }}
        >
          Approve Provider
        </Button>
      </Box>
    </Drawer>
  )
}
